import React, { useRef, useState, useEffect } from 'react';
import { formatDateTR, parseDateTR } from '../utils/dateUtils';

type TrDateInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> & {
    value?: string;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
};

/**
 * `<input type="date">` yerine kullanılabilen, tarihleri GG.AA.YYYY formatında
 * gösteren Türkçe tarih input bileşeni.
 *
 * - `value`: YYYY-MM-DD formatında giriş bekler
 * - `onChange`: e.target.value'yu YYYY-MM-DD formatında döndürür
 * - Tüm standart HTML input özellikleri (className, style, disabled, required, min, max, vb.) desteklenir
 */
const TrDateInput: React.FC<TrDateInputProps> = ({
    value,
    onChange,
    onBlur,
    min,
    max,
    style,
    className,
    disabled,
    required,
    id,
    name,
    placeholder,
    title,
    ...restProps
}) => {
    const hiddenRef = useRef<HTMLInputElement>(null);
    const [displayVal, setDisplayVal] = useState(() => formatDateTR(value));
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (!editing) {
            setDisplayVal(formatDateTR(value));
        }
    }, [value, editing]);

    const fireChange = (iso: string) => {
        onChange?.({ target: { value: iso } } as React.ChangeEvent<HTMLInputElement>);
    };

    const openPicker = () => {
        if (disabled || !hiddenRef.current) return;
        try {
            (hiddenRef.current as any).showPicker();
        } catch {
            hiddenRef.current.click();
        }
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditing(true);
        const digits = e.target.value.replace(/\D/g, '');
        let formatted = '';
        if (digits.length <= 2) {
            formatted = digits;
        } else if (digits.length <= 4) {
            formatted = `${digits.slice(0, 2)}.${digits.slice(2)}`;
        } else {
            formatted = `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4, 8)}`;
        }
        setDisplayVal(formatted);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setEditing(false);
        const iso = parseDateTR(displayVal);
        if (iso) {
            setDisplayVal(formatDateTR(iso));
            fireChange(iso);
        } else if (!displayVal.trim()) {
            fireChange('');
        } else {
            // Geçersiz giriş — önceki geçerli değere döndür
            setDisplayVal(formatDateTR(value));
        }
        onBlur?.(e);
    };

    const handleHiddenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const iso = e.target.value;
        setDisplayVal(formatDateTR(iso));
        fireChange(iso);
    };

    return (
        <span style={{ position: 'relative', display: 'inline-block' }}>
            <input
                {...restProps}
                type="text"
                id={id}
                name={name}
                value={displayVal}
                onChange={handleTextChange}
                onBlur={handleBlur}
                placeholder={placeholder ?? 'GG.AA.YYYY'}
                title={title}
                required={required}
                disabled={disabled}
                className={className}
                style={{ ...style, paddingRight: 26, boxSizing: 'border-box' }}
                autoComplete="off"
            />
            <button
                type="button"
                onClick={openPicker}
                tabIndex={-1}
                disabled={disabled}
                aria-label="Tarih seç"
                style={{
                    position: 'absolute',
                    right: 6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: disabled ? 'default' : 'pointer',
                    padding: 0,
                    color: '#9ca3af',
                    fontSize: 12,
                    lineHeight: 1,
                    pointerEvents: disabled ? 'none' : 'auto',
                    userSelect: 'none',
                }}
            >
                <i className="far fa-calendar-alt" />
            </button>
            <input
                ref={hiddenRef}
                type="date"
                value={value || ''}
                min={min}
                max={max}
                onChange={handleHiddenChange}
                tabIndex={-1}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    pointerEvents: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                }}
                aria-hidden="true"
            />
        </span>
    );
};

export default TrDateInput;
